import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SessionUser } from '@movo/contracts/api/auth';
import {
  changeFirstLoginPassword,
  fetchMyTenants,
  forgotPassword,
  login,
  logout,
  resetPassword,
  switchTenant,
} from './authApi';
import { clearToken, fetchSession, storeToken } from './session';

const sessionKeys = {
  user: ['session', 'user'] as const,
  allTenants: ['session', 'tenants'] as const,
  tenants: (userId: string | undefined) => ['session', 'tenants', userId] as const,
};

/** L'utente della sessione: `null` quando non c'è nessuno. Si legge una volta e si cambia solo con login e logout. */
export function useSessionQuery() {
  return useQuery({
    queryKey: sessionKeys.user,
    queryFn: fetchSession,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });
}

/** Il login salva il token e mette l'utente in sessione; risolve con `mustChangePassword`. */
export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: login,
    onSuccess: ({ token, user }) => {
      storeToken(token);
      queryClient.setQueryData<SessionUser | null>(sessionKeys.user, user);
    },
  });
}

/**
 * Chiude la sessione sul server (anche se non risponde) e svuota tutta la cache:
 * il prossimo utente non vede i dati del precedente.
 */
export function useLogoutMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      try {
        await logout();
      } catch {
        // Il refresh token resta revocabile lato server; il client esce comunque.
      }
    },
    onSettled: () => {
      clearToken();
      queryClient.setQueryData<SessionUser | null>(sessionKeys.user, null);
      queryClient.removeQueries({
        predicate: (query) => query.queryHash !== JSON.stringify(sessionKeys.user),
      });
    },
  });
}

export const useChangeFirstLoginPassword = () =>
  useMutation({ mutationFn: changeFirstLoginPassword });
export const useForgotPassword = () => useMutation({ mutationFn: forgotPassword });
export const useResetPassword = () => useMutation({ mutationFn: resetPassword });

/** I tenant dell'utente, solo con un utente in sessione. */
export function useMyTenantsQuery(userId: string | undefined) {
  return useQuery({
    queryKey: sessionKeys.tenants(userId),
    queryFn: fetchMyTenants,
    enabled: Boolean(userId),
  });
}

/** Rilegge i tenant della sessione: dopo che un admin crea o rinomina un tenant. */
export function useRefreshMyTenants() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: sessionKeys.allTenants });
}

// Il reload svuota tutta la cache: nessun dato del tenant precedente resta in memoria.
export const useSwitchTenant = () =>
  useMutation({
    mutationFn: switchTenant,
    onSuccess: (token) => {
      storeToken(token);
      window.location.reload();
    },
  });
